import { Fragment, PureComponent, SyntheticEvent } from 'react';

import { injectIntl, FormattedMessage, InjectedIntl } from 'react-intl';

import CustomThemeButton from '@atlaskit/button/custom-theme-button';
import { Checkbox } from '@atlaskit/checkbox';
import Modal, {
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '@atlaskit/modal-dialog';

import { ErrorState as ErrorStateBase } from 'src/components/generic-message';
import { commonMessages } from 'src/i18n';
import { publishUiEvent } from 'src/utils/analytics/publish';

import { WatchPrefs } from '../types';
import urls from '../urls';

import messages from './subscriptions-modal-dialog.i18n';

const settingsLabelMap = {
  pullrequests: 'labelPullrequests',
  issues: 'labelIssues',
  commits: 'labelCommits',
  wiki: 'labelWiki',
  forks: 'labelForks',
  commit_status: 'labelPipelines',
};

type Props = {
  hasError: boolean;
  intl: InjectedIntl;
  isWatchingRepository: boolean;
  onCloseSubscriptionsDialog: () => void;
  onFetchRepositorySubscriptions: (
    repositoryFullSlug: string,
    settings?: WatchPrefs
  ) => void;
  onToggleRepositoryWatch: (repositoryFullSlug: string) => void;
  repositoryFullSlug: string;
  settings?: WatchPrefs;
};

class SubscriptionsModalDialog extends PureComponent<Props> {
  componentDidMount() {
    const { onFetchRepositorySubscriptions, repositoryFullSlug } = this.props;
    if (repositoryFullSlug) {
      onFetchRepositorySubscriptions(repositoryFullSlug);
    }
  }

  // @ts-ignore TODO: fix noImplicitAny error here
  componentDidUpdate(prevProps) {
    const { onFetchRepositorySubscriptions, repositoryFullSlug } = this.props;
    if (
      !!repositoryFullSlug &&
      repositoryFullSlug !== prevProps.repositoryFullSlug
    ) {
      onFetchRepositorySubscriptions(repositoryFullSlug);
    }
  }

  handleOnCheckboxChange = (event: SyntheticEvent<HTMLInputElement>) => {
    if (!event.currentTarget) {
      return;
    }

    const { onFetchRepositorySubscriptions, repositoryFullSlug, settings } =
      this.props;

    const { checked: updatedValue, value: settingsKey } = event.currentTarget;
    const updatedSubscriptionSettings = {
      ...settings,
      ...{ [settingsKey]: updatedValue },
    };

    onFetchRepositorySubscriptions(
      repositoryFullSlug,
      updatedSubscriptionSettings
    );
  };

  renderCheckbox = (settingsKey: string) => {
    const { intl, isWatchingRepository, settings } = this.props;
    // @ts-ignore TODO: fix noImplicitAny error here
    const checkboxLabel = settingsLabelMap[settingsKey];

    return (
      <Checkbox
        // @ts-ignore TODO: fix noImplicitAny error here
        isChecked={settings[settingsKey]}
        isDisabled={!isWatchingRepository}
        // @ts-ignore TODO: fix noImplicitAny error here
        label={intl.formatMessage(messages[checkboxLabel])}
        onChange={this.handleOnCheckboxChange}
        value={settingsKey}
      />
    );
  };

  render() {
    if (!this.props.settings) {
      return null;
    }

    const {
      hasError,
      isWatchingRepository,
      onFetchRepositorySubscriptions,
      onToggleRepositoryWatch,
      onCloseSubscriptionsDialog,
      repositoryFullSlug,
      settings,
    } = this.props;

    const {
      has_commit_statuses: hasBuildsEnabled,
      has_issues: hasIssuesEnabled,
      has_repo: hasRepository,
      has_wiki: hasWikiEnabled,
    } = settings;

    const DialogHeading = (
      <FormattedMessage
        {...messages[
          isWatchingRepository ? 'headingWatching' : 'headingNotWatching'
        ]}
      />
    );

    const WatchButton = () => (
      <CustomThemeButton
        onClick={() => {
          publishUiEvent({
            action: 'clicked',
            actionSubject: 'button',
            actionSubjectId: 'watchRepositoryButton',
            source: 'repositorySubscriptionsModal',
            attributes: {
              item: isWatchingRepository
                ? 'unwatchRepository'
                : 'watchRepository',
            },
          });
          onToggleRepositoryWatch(repositoryFullSlug);
        }}
        aria-pressed={isWatchingRepository}
      >
        <FormattedMessage
          {...messages[
            isWatchingRepository
              ? 'buttonUnwatchRepository'
              : 'buttonWatchRepository'
          ]}
        />
      </CustomThemeButton>
    );

    const Checkboxes = () => (
      <Fragment>
        <p>
          <FormattedMessage
            tagName="strong"
            {...messages.headingSettingsList}
          />
        </p>
        {hasRepository && this.renderCheckbox('pullrequests')}
        {hasIssuesEnabled && this.renderCheckbox('issues')}
        {hasRepository && this.renderCheckbox('commits')}
        {hasWikiEnabled && this.renderCheckbox('wiki')}
        {hasRepository && this.renderCheckbox('forks')}
        {hasRepository &&
          hasBuildsEnabled &&
          this.renderCheckbox('commit_status')}
      </Fragment>
    );

    const ErrorState = () => (
      <ErrorStateBase
        // title={intl.formatMessage(messages.errorHeading)}
        description={
          <FormattedMessage tagName="p" {...messages.errorDescription} />
        }
        onClick={() => onFetchRepositorySubscriptions(repositoryFullSlug)}
      />
    );

    const DialogFooter = () => (
      <ModalFooter>
        <CustomThemeButton
          appearance="link"
          href={urls.ui.notifications}
          onClick={() =>
            publishUiEvent({
              action: 'clicked',
              actionSubject: 'link',
              actionSubjectId: 'manageAllSubscriptionsLink',
              source: 'repositorySubscriptionsModal',
              attributes: {
                item: 'manageAllSubscriptions',
              },
            })
          }
        >
          <FormattedMessage {...messages.actionManageNotifications} />
        </CustomThemeButton>
        <CustomThemeButton
          appearance="subtle"
          onClick={onCloseSubscriptionsDialog}
        >
          <FormattedMessage {...commonMessages.close} />
        </CustomThemeButton>
      </ModalFooter>
    );

    return (
      <Modal autoFocus={false} width="small">
        <ModalHeader>
          <ModalTitle>{DialogHeading}</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <WatchButton />
          {hasError ? <ErrorState /> : <Checkboxes />}
        </ModalBody>
        <DialogFooter />
      </Modal>
    );
  }
}

export default injectIntl(SubscriptionsModalDialog);
